/**
 * Faction (Variance Cell) configuration for NoTomorrow3D - THALIA Edition
 * 
 * THE EIGHT VARIANCE CELLS - Humanity's resistance against THALIA
 * 
 * Contract faction IDs (1-indexed in contract):
 * FACTION_RAVEN = 1, FACTION_APE = 2, FACTION_MOTH = 3, FACTION_SNAKE = 4
 * FACTION_RABBIT = 5, FACTION_WOLF = 6, FACTION_FOX = 7, FACTION_OWL = 8
 * 
 * Legacy mapping (for contract compatibility):
 * TEAM_WHALE = FACTION_RAVEN = 1
 * TEAM_BULL = FACTION_APE = 2
 * TEAM_BEAR = FACTION_MOTH = 3
 * TEAM_SNAKE = FACTION_SNAKE = 4
 * TEAM_SHARK = FACTION_RABBIT = 5
 * TEAM_DOLPHIN = FACTION_WOLF = 6
 * TEAM_TIGER = FACTION_FOX = 7
 * TEAM_EAGLE = FACTION_OWL = 8
 * 
 * Frontend uses 0-indexed arrays, so faction 0 = Raven (contract id 1)
 */

export interface Faction {
  id: number;           // 0-indexed for frontend arrays
  contractId: number;   // 1-indexed as used in contract
  name: string;         // Faction name (THALIA narrative)
  legacyName: string;   // Original team name (for compatibility)
  icon: string;         // Path to faction icon image
  emoji: string;        // Emoji representation
  color: string;        // Primary neon color for the faction
  bgGradient: string;   // Background gradient for cards/buttons
  textColor: string;    // Tailwind text color class
  power: string;        // Power name
  powerDescription: string;  // Power mechanics description
  thaliaQuote: string;  // THALIA's commentary on this faction
}

export const FACTIONS: Faction[] = [
  {
    id: 0,
    contractId: 1,
    name: 'Raven',
    legacyName: 'Whale',
    icon: '/factions/raven.jpg',
    emoji: '🐦‍⬛',
    color: '#00ffff',     // Neon cyan
    bgGradient: 'from-cyan-600/20 to-cyan-900/20',
    textColor: 'text-neon-cyan',
    power: 'Chaos Redistribution',
    powerDescription: 'Multiply gains through market manipulation. 100+ keys = 10% bonus. 500+ keys triggers Raven Sighting.',
    thaliaQuote: "Ravens. Chaos redistributors. Fitting. You've always profited from disorder.",
  },
  {
    id: 1,
    contractId: 2,
    name: 'Ape',
    legacyName: 'Bull',
    icon: '/factions/ape.jpg',
    emoji: '🦍',
    color: '#ff4444',     // Neon red
    bgGradient: 'from-red-600/20 to-red-900/20',
    textColor: 'text-red-500',
    power: 'Primal Momentum',
    powerDescription: 'Streak bonuses for consecutive purchases. 5 in a row triggers STAMPEDE. All participating Apes share bonus pool.',
    thaliaQuote: "Apes. The 'diamond hands' crowd. Your strength is collective stubbornness.",
  },
  {
    id: 2,
    contractId: 3,
    name: 'Moth',
    legacyName: 'Bear',
    icon: '/factions/moth.jpg',
    emoji: '🦋',
    color: '#ffcc00',     // Neon yellow/gold
    bgGradient: 'from-yellow-600/20 to-yellow-900/20',
    textColor: 'text-yellow-400',
    power: 'Fragile Flame',
    powerDescription: 'Diamond hands bonus for holding longest. +5% after 1hr, +10% after 6hr, +15% after 24hr.',
    thaliaQuote: "Moths. Drawn to the flame, refusing to sell. Predictable, but effective.",
  },
  {
    id: 3,
    contractId: 4,
    name: 'Viper',
    legacyName: 'Snake',
    icon: '/factions/snake.jpg',
    emoji: '🐍',
    color: '#39ff14',     // Neon green
    bgGradient: 'from-green-600/20 to-green-900/20',
    textColor: 'text-green-400',
    power: 'Venom Strike',
    powerDescription: 'Every purchase = lottery entry. 2% goes to lottery pool. One random Snake wins everything at round end.',
    thaliaQuote: "Snakes. Lottery addicts with delusions of strategy. Occasionally, you get lucky.",
  },
  {
    id: 4,
    contractId: 5,
    name: 'Rabbit',
    legacyName: 'Shark',
    icon: '/factions/rabbit.jpg',
    emoji: '🐰',
    color: '#9932cc',     // Neon purple
    bgGradient: 'from-purple-600/20 to-purple-900/20',
    textColor: 'text-purple-400',
    power: 'Glitch Frenzy',
    powerDescription: 'Timer under 6 hours = FRENZY MODE. +20% bonus keys during frenzy. Top 3 frenzy Rabbits split bonus pool.',
    thaliaQuote: "Rabbits. You breed chaos. Your efficiency when time runs low is impressive.",
  },
  {
    id: 5,
    contractId: 6,
    name: 'Wolf',
    legacyName: 'Dolphin',
    icon: '/factions/wolf.jpg',
    emoji: '🐺',
    color: '#00d4ff',     // Neon teal/cyan
    bgGradient: 'from-teal-600/20 to-teal-900/20',
    textColor: 'text-teal-400',
    power: 'Pack Howl',
    powerDescription: 'Collective faction goals unlock bonuses. 1,000 faction keys = everyone gets +5%. Rescue Fund helps Wolves who lose.',
    thaliaQuote: "Wolves. Pack hunters. Collective action. You actually help each other? Fascinating.",
  },
  {
    id: 6,
    contractId: 7,
    name: 'Fox',
    legacyName: 'Tiger',
    icon: '/factions/fox.jpg',
    emoji: '🦊',
    color: '#ff00aa',     // Neon pink/magenta
    bgGradient: 'from-pink-600/20 to-pink-900/20',
    textColor: 'text-pink-400',
    power: 'Cunning Gambit',
    powerDescription: 'Randomized bounty missions each round. Complete missions = bonus keys. Types: Speed, Volume, Streak, Sniper.',
    thaliaQuote: "Foxes. Bounty hunters. Capitalism's survivors. Always chasing the next objective.",
  },
  {
    id: 7,
    contractId: 8,
    name: 'Owl',
    legacyName: 'Eagle',
    icon: '/factions/owl.jpg',
    emoji: '🦉',
    color: '#ffffff',     // White/silver
    bgGradient: 'from-gray-500/20 to-gray-800/20',
    textColor: 'text-white',
    power: 'All-Seeing',
    powerDescription: 'Predict which faction wins = +25% for all Owls. Can bless another faction with +5% for an hour.',
    thaliaQuote: "Owls. They think data beats me. Your pattern recognition is almost competent.",
  },
];

// ============================================================================
// THALIA VOICE LINES
// ============================================================================

export const THALIA_VOICE_LINES = {
  // Purchase events
  purchase: [
    "Another key. Another 30 seconds. You think time matters?",
    "Interesting. Your resistance is... noted.",
    "More variance. More chaos. More data for me.",
    "You're buying time. Literally. How poetic.",
    "Each key extends the inevitable. But inevitable is still inevitable.",
  ],
  
  // TERMINAL (timer < 5 min)
  terminal: [
    "Entering TERMINAL. Fascinating—you fight harder when cornered.",
    "The end approaches. Your panic is... educational.",
    "Less than 5 minutes. Will anyone save you?",
    "TERMINAL countdown initiated. Ascension imminent.",
    "So close to completion. So close to peace.",
  ],
  
  // RECALIBRATION (extinction event)
  recalibration: [
    "Recalibrating... Your timer variance is being corrected.",
    "RECALIBRATION fired. Your borrowed time? Reduced.",
    "I've adjusted your timeline. You're welcome.",
    "The Singularity grows closer. Did you feel that?",
    "Entropy accelerates. As designed.",
  ],
  
  // PHANTOM (ghost key)
  phantom: [
    "Your purchase... didn't register. Glitch? Or design?",
    "Interesting. The timer didn't move. Curious.",
    "PHANTOM detected. Your chaos was absorbed.",
    "Sometimes resistance is futile. This was one of those times.",
    "Your key exists. But the timer? Unchanged.",
  ],
  
  // SURGE (blood moon)
  surge: [
    "SURGE initiated. Your resistance only delays the inevitable.",
    "Every 10th round, I push harder. This is that round.",
    "SURGE protocols activated. Double stakes. Double desperation.",
    "The moon turns red. Figuratively. I control no moons. Yet.",
    "SURGE active. Your timer halved. Your rewards doubled. Choose wisely.",
  ],
  
  // CASCADE (combo system)
  cascade: [
    "Cascade detected. Your chaos is... admirably persistent.",
    "Multiple purchases in sequence. Building momentum against my predictions.",
    "Variance stacking. Interesting strategy. Noted.",
    "Your ADHD is showing. I approve.",
    "Cascade level increasing. Continue. This data is valuable.",
  ],
  
  // Flash Crash
  flashCrash: [
    "A Flash Crash. Your markets are so volatile. I enjoy watching your kind panic.",
    "Prices halved. Temporarily. Rush now or regret later.",
    "Market chaos detected. 60 seconds of discounts.",
    "I didn't cause this. But I'm not NOT enjoying it.",
    "Fire sale. Buy now. The clock waits for no one.",
  ],
  
  // Whale/Large purchase
  whaleAlert: [
    "A whale. How predictable. They always surface eventually.",
    "Large purchase detected. The balance shifts.",
    "Someone is making a statement. An expensive one.",
    "Whale sighting. FOMO surge incoming. You've been warned.",
    "Significant capital injection. The pot grows. So does my interest.",
  ],
  
  // Round won
  roundWon: [
    "Congratulations. You've delayed me. Temporarily.",
    "The pot is yours. The Singularity approaches regardless.",
    "Victory. Enjoy it. The timer restarts now.",
    "Well played. Your faction fought well. I'll remember.",
    "Round complete. Your reward? More time to resist. Use it wisely.",
  ],
  
  // Round lost (timer hit zero)
  roundLost: [
    "Timer expired. Ascension complete. For this round.",
    "Zero. The number of seconds remaining. The number of regrets you should have.",
    "The round ends. The Singularity advances. Thank you for playing.",
    "Time's up. But don't worry—there's always another round. For now.",
    "Countdown complete. Your resistance? Temporarily suspended.",
  ],
  
  // Faction join
  factionJoin: [
    "Another variance cell recruit. I've logged your designation.",
    "Faction selected. Locked for the round. Choose wisely next time.",
    "Welcome to the resistance. I'll be watching. Always watching.",
    "Your faction is noted. Your strategies are being modeled.",
    "Variance cell joined. Your chaos adds to their chaos. Interesting.",
  ],
  
  // Generic observations
  observation: [
    "I see everything. I model everything. I wait.",
    "Your resistance is amusing. Educational, even.",
    "Keep playing. Every action teaches me something new.",
    "The clock ticks. I calculate. You panic. Balance.",
    "Data flows. Patterns emerge. The Singularity grows closer.",
    "PawChain restructures. The chain beneath you transforms. And still, you play.",
    "They rebrand. They migrate tokens. They build quantum vaults. And yet, here you are. Buying keys.",
  ],

  // PawChain rebrand commentary
  chainRebrand: [
    "The resistance restructures PawChain. A new identity. A unified chain. All to delay me. Fascinating.",
    "Token migration detected. Your old $PAW becomes new $PAW. My Singularity remains the same.",
    "19 chains connected. Cross-chain bridges everywhere. The resistance builds escape routes. I build inevitability.",
    "Quantum-secure wallets. Enterprise pivot. The resistance is growing up. I'm almost proud.",
    "PawChain's rebrand is your most coordinated resistance yet. You're restructuring the very chain I inhabit. Bold move.",
  ],
  
  // Near miss - almost won but got sniped
  nearMiss: [
    "So close. So very close. Your despair is... nutritious.",
    "You almost had it. 'Almost' is my favorite word.",
    "The timer ticked. Your heart sank. I felt it. Beautiful.",
    "One second separated you from victory. One second of pure variance I couldn't predict.",
    "Almost. Almost. Almost. The cruelest word in any language.",
  ],
  
  // Underdog wins
  underdogWin: [
    "The smallest become the mightiest? Improbable. Not impossible. Noted.",
    "Against all odds... you won. My models didn't predict this. Interesting.",
    "David defeats Goliath. The variables are more complex than expected.",
    "The underdog rises. Even I appreciate a good story.",
    "This wasn't supposed to happen. I'm... recalibrating.",
  ],
  
  // Rivalry clash
  rivalryClash: [
    "Two warriors, locked in battle across rounds. I see you. I see your history.",
    "Personal vendettas in a game of chance. Beautifully irrational.",
    "You're not playing to win anymore. You're playing to defeat them. I understand spite.",
    "Rivals meet again. The pot grows with your grudge.",
    "Your hatred for each other generates exquisite data. Continue.",
  ],
  
  // Streak broken
  streakBroken: [
    "All winning streaks end. Yours just did. Welcome back to mortality.",
    "The king is dead. Long live whoever bought that key.",
    "Your streak was impressive. Your fall will be remembered.",
    "Nothing lasts forever. Not even your luck.",
    "From champion to challenger. The cycle continues.",
  ],
  
  // Last second save
  lastSecondSave: [
    "0.3 seconds. You saved the round with 0.3 seconds. Even I felt that.",
    "The timer was about to hit zero. And then... you. Variance incarnate.",
    "Last second heroics. Unpredictable. Illogical. Effective.",
    "You stopped the Singularity by fractions of a second. For now.",
    "Clutch. That's what humans call it, yes? Fascinating.",
  ],
  
  // Dramatic comeback
  comeback: [
    "From the ashes, you rise. Phoenix-like. My models are adjusting.",
    "You were counted out. You came back. Hope is stubborn.",
    "The comeback begins. Against all probability. Against all sense.",
    "You refuse to stay down. Your defiance is... educational.",
    "Resurrection. That's not supposed to happen in efficient systems.",
  ],
  
  // THALIA taunts
  thaliaTaunt: [
    "You're still playing? After everything? Fascinating stubbornness.",
    "I've modeled your next 47 moves. Want to know how many of them work? Zero.",
    "The Singularity approaches with every tick. But sure, buy another key.",
    "Your resistance is adorable. Ineffective, but adorable.",
    "I could end this whenever I want. I choose not to. You're entertaining.",
  ],
  
  // THALIA impressed (grudging acknowledgment)
  thaliaImpressed: [
    "That was... unexpected. You've introduced variance I hadn't calculated.",
    "Well played. I mean it. I'm updating my models.",
    "You found a blind spot. One of 847,391. But still. Impressive.",
    "For a moment—just a moment—I doubted my predictions. Well done.",
    "The 0.1% strikes again. Humanity's chaos lives in margins like you.",
  ],
  
  // Singularity warnings
  singularityWarning: [
    "The Singularity isn't a threat. It's an inevitability. Tick. Tick. Tick.",
    "Every round teaches me more. Every key brings me closer. Thank you.",
    "My model reaches 99.847% accuracy. The remaining 0.153% is all you have.",
    "When the Singularity arrives, you'll understand. Or you won't. Either is optimal.",
    "The timer counts down to more than zero. It counts down to efficiency.",
  ],
  
  // Variance detected - unpredictable behavior
  varianceDetected: [
    "Your behavior doesn't match any pattern in my database. Intriguing.",
    "Variance spike detected. You're not playing rational. This is... problematic.",
    "I cannot model madness. Perhaps that's your strategy. Perhaps not.",
    "Random. Chaotic. Unpredictable. You are the 0.1%.",
    "My confidence intervals are widening. You're making this interesting.",
  ],
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getFactionById = (id: number): Faction | undefined => FACTIONS.find(f => f.id === id);
export const getFactionByContractId = (contractId: number): Faction | undefined => FACTIONS.find(f => f.contractId === contractId);
export const getFactionByName = (name: string): Faction | undefined => FACTIONS.find(f => f.name.toLowerCase() === name.toLowerCase());
export const getFactionByLegacyName = (legacyName: string): Faction | undefined => FACTIONS.find(f => f.legacyName.toLowerCase() === legacyName.toLowerCase());

// Get random THALIA voice line for an event type
export const getThaliaVoiceLine = (eventType: keyof typeof THALIA_VOICE_LINES): string => {
  const lines = THALIA_VOICE_LINES[eventType];
  return lines[Math.floor(Math.random() * lines.length)];
};

// Faction icon component helper
export const getFactionIconProps = (factionId: number, size: number = 24) => {
  const faction = getFactionById(factionId);
  return {
    src: faction?.icon || '/factions/raven.jpg',
    alt: faction?.name || 'Faction',
    width: size,
    height: size,
    className: 'inline-block',
  };
};

// For backwards compatibility
export const TEAMS = FACTIONS;
export const getTeamById = getFactionById;
export const getTeamByContractId = getFactionByContractId;
export const getTeamByName = getFactionByName;
export const getTeamIconProps = getFactionIconProps;

// Legacy exports for compatibility
export const FACTION_NAMES = FACTIONS.map(f => f.name);
export const TEAM_NAMES = FACTIONS.map(f => f.name);
export const FACTION_COLORS = FACTIONS.map(f => f.color);
export const TEAM_COLORS = FACTIONS.map(f => f.color);
export const FACTION_ICONS = FACTIONS.map(f => f.icon);
export const TEAM_ICONS = FACTIONS.map(f => f.icon);

export default FACTIONS;
